import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { from, NEVER, Observable, of, race, Subject } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import {
  AysVirtualClinic,
  AysVirtualClinicSpecialtyDepartment,
} from '@bindoc-projects/virtual-clinic-domain-lib';
import { VirtualClinicService } from '../../../core';
import { BdUiMultiSelectComponent, BdUiMultiSelectConfig } from '@bindoc-projects/ui-lib/selection/ui';

@Component({
    selector: 'ays-virtual-clinic-specialty-departments-inline-multiple-select',
    template: `
    <bd-ui-multi-select
      [config]="config"
      [initialSelection]="initialSpecialtyDepartments()"
      [selectOptions]="specialtyDepartmentOptions"
      (selection)="setSpecialtyDepartments($event)"
    ></bd-ui-multi-select>
  `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [BdUiMultiSelectComponent]
})
export class AysVirtualClinicSpecialtyDepartmentsInlineMultipleSelectComponent {
  private readonly _virtualClinicService = inject(VirtualClinicService);

  readonly virtualClinicId = input<string | undefined>(undefined);
  readonly selectedSpecialtyDepartmentIds = input<string[]>([]);
  readonly specialtyDepartmentSelect = output<AysVirtualClinicSpecialtyDepartment[]>();

  public readonly config: BdUiMultiSelectConfig<AysVirtualClinicSpecialtyDepartment> =
    {
      id: 'virtual-clinic-specialty-departments-inline-multiple-select',
      selectLabel: { '=1': 'Fachabteilung', other: 'Fachabteilungen' },
      option: {
        sortFnc: (a, b) => this.sortFnc(a, b),
        uniqueFnc: (option) => option.id,
        searchOptionAccessor: (option) => `${option.fabKey} ${option.name}`,
        searchOptionDisplayName: (option) => option?.name ?? option.fabKey,
      },
    };

  readonly initialSpecialtyDepartments = signal<AysVirtualClinicSpecialtyDepartment[]>([]);
  public specialtyDepartmentOptions: Observable<AysVirtualClinicSpecialtyDepartment[]> = NEVER;

  private readonly _newVirtualClinicChange = new Subject<AysVirtualClinic>();

  constructor() {
    effect(() => {
      const virtualClinicId = this.virtualClinicId();
      if (virtualClinicId) {
        this._newVirtualClinicChange.next(virtualClinicId as unknown as AysVirtualClinic);
        this._fetch(virtualClinicId);
      } else {
        this.specialtyDepartmentOptions = of([]);
      }
    });

    effect(() => {
      const ids = this.selectedSpecialtyDepartmentIds();
      this.initialSpecialtyDepartments.set(
        ids.map(
          (specialtyDepartmentId) =>
            new AysVirtualClinicSpecialtyDepartment(null, specialtyDepartmentId),
        ),
      );
    });
  }

  public setSpecialtyDepartments(
    specialtyDepartments: AysVirtualClinicSpecialtyDepartment[],
  ) {
    this.specialtyDepartmentSelect.emit(specialtyDepartments);
  }

  private _fetch(virtualClinicId: string) {
    // fetching the specialty departments needs some time
    // virtual clinics changes sometimes (in case of first select) faster,
    // so create race condition to stop the running fetch process (for the old selected virtual clinic) with empty array
    // and let the new virtual clinic fetch their specialty departments in its fetch() call
    this.specialtyDepartmentOptions = race(
      from(
        this._virtualClinicService.getSpecialtyDepartments(virtualClinicId),
      ),
      this._newVirtualClinicChange.asObservable(),
    ).pipe(
      switchMap((value) => {
        if (value instanceof Array) {
          return of(value);
        } else {
          return of([]);
        }
      }),
    );
  }

  public sortFnc(
    a: AysVirtualClinicSpecialtyDepartment,
    b: AysVirtualClinicSpecialtyDepartment,
  ): number {
    return !!a && !!b && a?.name?.localeCompare(b?.name);
  }
}
